import cn from 'classnames';
import { ChangeEvent, FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AccountSettings } from '@/shared/api/protocol-ts/api/billing_new/dto_account_new_pb';
import { SetAccountSettingsReq } from '@/shared/api/protocol-ts/api/billing_new/svc_billing_new_pb';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, Icon, Switch } from '@/shared/ui';

import { billingModel } from '@/entities/billing';
import { composedModel } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';

import styles from './Diagnocoins.module.scss';

type DiagnocoinsProps = {
  className?: string;
  testID?: string;
};

export const Diagnocoins: FC<DiagnocoinsProps> = (props) => {
  const { className, testID } = props;

  const { formatMessage, formatDate } = useIntl();
  const dispatch = useAppDispatch();

  const account = useAppSelector(billingModel.selectors.selectAccount);
  const available = useAppSelector(
    billingModel.selectors.selectDiagnocoinAvailable,
  );
  const reserved = useAppSelector(
    billingModel.selectors.selectDiagnocoinReserved,
  );
  const batches = useAppSelector(
    billingModel.selectors.selectActiveDiagnocoinBatches,
  );
  const expiringSoon = useAppSelector(
    billingModel.selectors.selectExpiringSoonDiagnocoins,
  );
  const isDiagnocoinsEnabled = useAppSelector(
    composedModel.selectors.selectIsDiagnocoinsEnabled,
  );
  const currentOrganization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );

  const permissions = currentOrganization?.YourPermissions;
  const isAutoUseEnabled = Boolean(account?.Settings?.AutoUseDiagnocoins);

  const handleOpenHistory = () => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.DiagnocoinHistoryModal,
        data: {},
      }),
    );
  };

  const handleAutoUseChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(
      billingModel.thunks.setAccountSettings(
        new SetAccountSettingsReq({
          AccountID: account.ID,
          Settings: new AccountSettings({
            ...account.Settings,
            AutoUseDiagnocoins: event.target.checked,
          }),
        }),
      ),
    );
  };

  if (!isDiagnocoinsEnabled || !permissions?.BillingCanViewInventory) {
    return null;
  }

  return (
    <div
      data-testid={testID}
      className={cn(styles.container, className)}
      data-section="diagnocoins-balance"
    >
      <header className={styles.header}>
        <h4 className="h4">
          {formatMessage({
            id: 'diagnocoins.header',
            defaultMessage: 'Diagnocoins',
          })}
        </h4>
      </header>

      <div className={styles.balance}>
        <span className={styles.glyph}>
          <Icon name="cat" size={24} />
        </span>

        <div className={styles.amount}>
          <span className="h3">{available}</span>
          <span className={cn(styles.availableLabel, 'p2')}>
            {formatMessage({
              id: 'diagnocoins.available',
              defaultMessage: 'coins available',
            })}
          </span>
        </div>

        {reserved > 0 && (
          <span className={cn(styles.reserved, 'p3')}>
            {formatMessage(
              {
                id: 'diagnocoins.reserved',
                defaultMessage:
                  '{reserved, plural, one {# coin} other {# coins}} held by reports in progress',
              },
              { reserved },
            )}
          </span>
        )}
      </div>

      {batches.length > 0 && (
        <div className={styles.batches}>
          {batches.map((batch) => {
            const purchasedAt = batch.PurchasedAt?.toDate();
            const expiresAt = batch.ExpiresAt?.toDate();
            const isExpiringSoon =
              expiringSoon !== null &&
              expiresAt !== undefined &&
              expiresAt <= expiringSoon.threshold;

            return (
              <div key={batch.ID} className={styles.batch}>
                <span className="p2">
                  {formatMessage(
                    {
                      id: 'diagnocoins.batch.purchased',
                      defaultMessage:
                        'Purchased {date} · {total, plural, one {# coin} other {# coins}}',
                    },
                    {
                      date:
                        purchasedAt === undefined
                          ? '—'
                          : formatDate(purchasedAt, { dateStyle: 'medium' }),
                      total: Number(batch.Total),
                    },
                  )}
                </span>

                <span className={cn(styles.batchRemaining, 'p3')}>
                  <FormattedMessage
                    id="diagnocoins.batch.remaining"
                    defaultMessage="{remaining} left · expires <highlight>{date}</highlight>"
                    values={{
                      remaining: Number(batch.Remaining),
                      date:
                        expiresAt === undefined
                          ? '—'
                          : formatDate(expiresAt, { dateStyle: 'medium' }),
                      highlight: (chunks) => (
                        <span
                          className={cn(isExpiringSoon && styles.expiringSoon)}
                        >
                          {chunks}
                        </span>
                      ),
                    }}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.autoDeduction}>
        <div className={styles.autoDeductionText}>
          <h5 className={cn('p2', 'bold')}>
            {formatMessage({
              id: 'diagnocoins.autoDeduction.title',
              defaultMessage: 'Automatic deduction',
            })}
          </h5>

          <p className={cn(styles.autoDeductionDescription, 'p3')}>
            {formatMessage({
              id: 'diagnocoins.autoDeduction.description',
              defaultMessage:
                'Let the API and the desktop app pay for reports with coins once the subscription limit runs out, without asking each time. Orders placed here in the browser always ask first, whatever this is set to.',
            })}
          </p>
        </div>

        <Switch
          checked={isAutoUseEnabled}
          disabled={!permissions?.BillingCanEditAccountSettings}
          onChange={handleAutoUseChange}
        />
      </div>

      <Button
        size="medium"
        variant="tertiary"
        className={styles.history}
        onClick={handleOpenHistory}
      >
        {formatMessage({
          id: 'diagnocoins.viewHistory',
          defaultMessage: 'View transaction history',
        })}
      </Button>
    </div>
  );
};
